// "Levelek" on the contact's page (5.5): every campaign this contact was in,
// newest first — what was sent and what became of it, and (rule 6) what was
// not sent and why: skipped, failed, cancelled, still waiting, with the reason
// the row holds. Over the list, the figures the segment builder filters by.
// Provisional and thin: the existing table, tiles and pills, no chart.
//
// The figures are the nightly `contact_engagement` row — our own sends only,
// counted per message, opened only when a person opened it (a Gmail proxy
// fetch is one; Apple Mail's prefetch and the scanners are not). The list is
// live: a message sent today is here before it is in the figures, and the
// card says when the figures were made.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { ApiError, fetchContactMessages } from "@/lib/api";
import type { ContactEngagementFigures, ContactMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, TablePager } from "@/components/emc/DataTable";
import { StatGrid, StatTile } from "@/components/emc/StatTile";
import { StatusPill } from "@/components/emc/StatusPill";
import { EmptyState, ErrorState } from "@/components/emc/states";

function when(value: string | null): string {
  return value === null ? "—" : new Date(value).toLocaleString("hu-HU");
}

function problem(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) return error.message;
  return "Ismeretlen hiba.";
}

const NONE = <span className="text-muted-foreground">—</span>;

const COLUMNS: ColumnDef<ContactMessage>[] = [
  {
    id: "campaign",
    header: "Kampány",
    // The campaign page is the group's; a campaign without one has no page.
    cell: ({ row }) => (
      <span>
        {row.original.group_id === null ? (
          row.original.campaign_name
        ) : (
          <Link to={`/kampanyok/${row.original.group_id}`}>{row.original.campaign_name}</Link>
        )}{" "}
        <span className="data-mono text-muted-foreground">#{row.original.campaign_id}</span>
      </span>
    ),
  },
  {
    id: "sent",
    header: "Elküldve",
    cell: ({ row }) => when(row.original.sent_at),
    meta: { mono: true, muted: true },
  },
  {
    id: "delivery",
    header: "Kézbesítés",
    // What went says what became of it. What did not go — or may not have:
    // an `unknown` nobody confirmed — says that instead, and why when the row
    // knows.
    cell: ({ row }) =>
      row.original.sent ? (
        <StatusPill domain="delivery" state={row.original.delivery_state} />
      ) : (
        <span className="inline-flex flex-wrap items-center gap-2">
          <StatusPill domain="recipient" state={row.original.state} />
          {row.original.reason !== null && <span className="text-muted-foreground">{row.original.reason}</span>}
        </span>
      ),
    meta: { wrap: true },
  },
  {
    id: "opened",
    header: "Megnyitás",
    cell: ({ row }) =>
      row.original.sent ? <StatusPill domain="messageOpened" state={row.original.opened} /> : NONE,
  },
  {
    id: "openedAt",
    header: "Először megnyitva",
    cell: ({ row }) => when(row.original.opened_at),
    meta: { mono: true, muted: true },
  },
  {
    id: "clicked",
    header: "Kattintott",
    cell: ({ row }) => (row.original.clicked ? when(row.original.clicked_at) : "—"),
    meta: { mono: true, muted: true },
  },
  {
    id: "pool",
    header: "IP-pool",
    cell: ({ row }) => row.original.pool ?? "—",
    meta: { mono: true, muted: true },
  },
];

function Figures({ figures, lastHumanOpen }: { figures: ContactEngagementFigures; lastHumanOpen: string | null }) {
  return (
    <StatGrid>
      <StatTile label="Utolsó levelünk" value={when(figures.last_sent_at)} size="sm" />
      <StatTile
        label="Kapott levél (30 / 90 nap / 1 év)"
        value={`${figures.received_30d} / ${figures.received_90d} / ${figures.received_365d}`}
        comparison={`összesen ${figures.received_total}`}
        size="sm"
      />
      <StatTile
        label="Ember nyitotta meg (30 / 90 nap)"
        value={`${figures.opened_30d} / ${figures.opened_90d}`}
        comparison={`kattintott: ${figures.clicked_30d} / ${figures.clicked_90d}`}
        size="sm"
      />
      <StatTile
        label="Az utolsó 5 / 10 levélből megnyitott"
        value={`${figures.last5_opened} / ${figures.last10_opened}`}
        comparison={`kattintott: ${figures.last5_clicked} / ${figures.last10_clicked}`}
        size="sm"
      />
      <StatTile
        label="Utolsó valódi megnyitás · kattintás"
        value={when(lastHumanOpen)}
        comparison={when(figures.last_human_click_at)}
        size="sm"
      />
    </StatGrid>
  );
}

export function ContactMessages({ contactId }: { contactId: number }) {
  const [items, setItems] = useState<ContactMessage[]>([]);
  const [figures, setFigures] = useState<ContactEngagementFigures | null>(null);
  const [lastHumanOpen, setLastHumanOpen] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function load(from: string | null) {
    setLoading(true);
    fetchContactMessages(contactId, from)
      .then((body) => {
        setItems((rows) => (from === null ? body.items : [...rows, ...body.items]));
        setFigures(body.figures);
        setLastHumanOpen(body.last_human_open_at);
        setCursor(body.next_cursor);
        setError(null);
      })
      .catch((reason: unknown) => setError(problem(reason)))
      .finally(() => setLoading(false));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => load(null), [contactId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Levelek</CardTitle>
        <CardDescription>
          Minden kampányunk, amelyben ez a kontakt benne volt, a legfrissebb elöl — az is, amelyik végül nem ment
          ki neki, és hogy miért (kihagyva, hiba, leállítva, még vár). A számok csak a saját küldéseinkből
          vannak, levelenként: megnyitottnak az számít, amit ember nyitott meg (vagy kattintott benne) — az Apple
          Mail automatikus letöltése és a levélszűrők nem, a Gmail képproxyja igen. A számokat éjszakánként
          frissítjük{figures === null ? "" : ` (utoljára: ${when(figures.computed_at)})`}, a lista élő.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {error !== null && <ErrorState message={error} />}
        {figures !== null && <Figures figures={figures} lastHumanOpen={lastHumanOpen} />}
        {!loading && items.length === 0 && error === null ? (
          <EmptyState title="Ez a kontakt még egy kampányunkban sem volt." />
        ) : (
          <DataTable
            label="Levelek"
            variant="plain"
            className="-mx-5.5 border-t border-separator"
            columns={COLUMNS}
            data={items}
            getRowId={(row) => String(row.campaign_id)}
            loading={loading && items.length === 0}
            footer={
              <TablePager summary={`${items.length} levél${cursor === null && items.length > 0 ? " · ennyi van" : ""}`}>
                <Button
                  variant="outline"
                  type="button"
                  disabled={cursor === null || loading}
                  onClick={() => load(cursor)}
                >
                  Továbbiak
                </Button>
              </TablePager>
            }
          />
        )}
      </CardContent>
    </Card>
  );
}
